/**
 * Custom element that controls when a hero/slide <video> starts loading.
 *
 * Attributes
 * ----------
 * data-priority  "high" | "medium" | "low"
 *   high   — activate as soon as the viewport check passes (no IntersectionObserver)
 *   medium — activate via IntersectionObserver at 300 px rootMargin
 *   low    — activate via IntersectionObserver at 50 px rootMargin, sources in data-src
 *
 * data-media  "desktop" | "mobile" | (absent = both)
 *   Prevents the video from loading on the wrong viewport.
 *   "desktop" → only loads when min-width: 750px
 *   "mobile"  → only loads when max-width: 749px
 *   This is the only reliable way to stop Chrome from fetching hidden
 *   mobile/desktop videos: CSS `display: none` does NOT prevent video
 *   source downloads; omitting `src` from the DOM does.
 *
 * Why not `autoplay` in HTML?
 * Chrome ignores preload="none" for `autoplay muted` elements and still
 * issues range requests. By omitting `autoplay` from the HTML and calling
 * play() from JS only after activation, we prevent that pre-fetch entirely.
 *
 * iOS Low Power Mode fallback
 * When video.play() rejects with NotAllowedError (iOS Low Power Mode blocks
 * autoplay even for muted videos), a play button overlay is injected into the
 * container. Clicking it triggers video.play() via a user gesture, which iOS
 * allows regardless of Low Power Mode.
 */

// Inject play-button overlay styles once per page load.
(function () {
  if (document.getElementById('hero-video-play-btn-styles')) return;
  const style = document.createElement('style');
  style.id = 'hero-video-play-btn-styles';
  style.textContent = `
    .hero-video-play-btn {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      z-index: 3;
      pointer-events: auto;
    }
    .hero-video-play-btn__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.45);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      color: #fff;
      transition: transform 0.2s ease, background-color 0.2s ease;
    }
    .hero-video-play-btn:hover .hero-video-play-btn__icon,
    .hero-video-play-btn:focus .hero-video-play-btn__icon {
      background: rgba(0, 0, 0, 0.65);
      transform: scale(1.08);
    }
    .hero-video-play-btn:focus-visible {
      outline: 3px solid rgba(255, 255, 255, 0.85);
      outline-offset: -6px;
      border-radius: 4px;
    }
  `;
  document.head.appendChild(style);
})();

// Tracks videos that already have a play button showing, avoiding duplicates
// without mutating the DOM element.
/** @type {WeakSet<HTMLVideoElement>} */
const _playBtnActive = new WeakSet();

class HeroVideoLoader extends HTMLElement {
  connectedCallback() {
    const media = this.dataset.media; // 'desktop' | 'mobile' | undefined

    if (this._mediaMatches(media)) {
      this._setup();
    } else {
      // Wrong viewport right now — watch for resize/orientation changes.
      this._watchMedia(media ?? 'desktop');
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    if (this._mq && this._mqListener) {
      this._mq.removeEventListener('change', this._mqListener);
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** @param {string | undefined} media */
  _mediaMatches(media) {
    if (media === 'desktop') return window.matchMedia('(min-width: 750px)').matches;
    if (media === 'mobile') return window.matchMedia('(max-width: 749px)').matches;
    return true;
  }

  /** @param {string} media */
  _watchMedia(media) {
    const query = media === 'mobile' ? '(max-width: 749px)' : '(min-width: 750px)';
    const mq = window.matchMedia(query);
    this._mq = mq;
    /** @param {MediaQueryListEvent} e */
    const listener = (e) => {
      if (!e.matches) return;
      mq.removeEventListener('change', listener);
      this._setup();
    };
    this._mqListener = listener;
    mq.addEventListener('change', listener);
  }

  _setup() {
    const priority = this.dataset.priority;

    if (priority === 'high') {
      // Activate immediately — this viewport is correct and the element is
      // flagged as above-fold. No IntersectionObserver needed.
      this._activate();
      return;
    }

    // `hero-video-loader` itself is `display: contents` and generates no box,
    // so an IntersectionObserver on `this` never reports isIntersecting.
    // Observe the <video> element instead, which has real geometry.
    const video = this.querySelector('video');
    if (!video) return;

    const rootMargin = priority === 'medium' ? '300px 0px' : '50px 0px';
    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          this._observer?.disconnect();
          this._activate();
        }
      },
      { rootMargin }
    );
    this._observer.observe(video);
  }

  _activate() {
    const video = /** @type {HTMLVideoElement | null} */ (this.querySelector('video'));
    if (!video) return;

    // Low priority stores sources in data-src to keep them invisible to the
    // browser preload scanner. Move them to src before load().
    for (const source of /** @type {NodeListOf<HTMLSourceElement>} */ (this.querySelectorAll('source[data-src]'))) {
      const src = source.getAttribute('data-src');
      if (src) {
        source.setAttribute('src', src);
        source.removeAttribute('data-src');
      }
    }

    video.load();

    // Explicitly call play() after load(). The `autoplay` HTML attribute is
    // intentionally absent — Chrome still pre-fetches bytes for `autoplay muted`
    // elements even with preload="none". play() here is reliable once the
    // element is in the correct viewport and near/in the visible area.
    //
    // iOS Low Power Mode blocks autoplay in two ways depending on iOS version:
    //
    //   iOS 16+  — play() REJECTS with NotAllowedError. Caught in .catch().
    //
    //   iOS 10–15 — play() RESOLVES but the browser immediately re-pauses the
    //   video without throwing. One requestAnimationFrame after resolution lets
    //   the engine process any synchronous pause, then video.paused reveals it.
    //
    // Both paths call _showPlayButton() so the overlay appears on all versions.
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          requestAnimationFrame(() => {
            if (video.paused) this._showPlayButton(video);
          });
        })
        .catch((err) => {
          if (err.name === 'NotAllowedError') {
            this._showPlayButton(video);
          }
          // NotSupportedError, AbortError — not user-actionable, no UI.
        });
    } else {
      // iOS < 10: play() returns undefined. Check paused after a tick.
      setTimeout(() => {
        if (video.paused) this._showPlayButton(video);
      }, 100);
    }
  }

  /** @param {HTMLVideoElement} video */
  _showPlayButton(video) {
    if (_playBtnActive.has(video)) return;
    _playBtnActive.add(video);

    const btn = document.createElement('button');
    btn.className = 'hero-video-play-btn';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Play video');
    // SVG play triangle — aria-hidden so screen readers use the button label only.
    btn.innerHTML = `<span class="hero-video-play-btn__icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="36" height="36" focusable="false">
        <path d="M8 5v14l11-7z"/>
      </svg>
    </span>`;

    btn.addEventListener('click', () => {
      btn.remove();
      _playBtnActive.delete(video);
      // User gesture satisfies iOS autoplay policy even in Low Power Mode.
      video.play().catch(() => {
        // If play still fails (e.g. network error after user tap), re-show
        // the button so the user can try again rather than seeing a black frame.
        this._showPlayButton(video);
      });
    });

    // hero-video-loader has display:contents so it forms no box of its own.
    // Append the button to the nearest real container (the positioned parent)
    // so `position: absolute; inset: 0` places it over the video correctly.
    this.parentElement?.appendChild(btn);
  }
}

if (!customElements.get('hero-video-loader')) {
  customElements.define('hero-video-loader', HeroVideoLoader);
}
